import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getrapporttwin } from '../../Redux/Actions/RapportTwinAction';
import { getchargexp } from '../../Redux/Actions/ChargeExpActions';
import axios from 'axios';
import moment from 'moment';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import './TwinparkStats.css';

const TwinparkStats = () => {
  const dispatch = useDispatch();
  const token = localStorage.getItem('token');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);
  };

  const getPercentage = (value, total) => {
    if (!total || total === 0) return "0.0%";
    return ((value / total) * 100).toFixed(1) + "%";
  };
  
  // States
  const [localisation, setLocalisation] = useState("All");
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [twinToysPosData, setTwinToysPosData] = useState({});

  useEffect(() => {
    dispatch(getrapporttwin());
    dispatch(getchargexp());
  }, [dispatch]);

  const rapports = useSelector((state) => state.RapportTwinReducer.RapportsTwin || []);
  const chargesExp = useSelector((state) => state.ChargeExpReducer.ChargeExpl || []);

  // API Call for Advanced Comparison
  const fetchAnalytics = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/analytics/summary', {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate, endDate }
      });
      setSummaryData(res.data);
    } catch (error) {
      console.error("Error fetching analytics", error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const fetchTwinToysPosAndCost = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      // Pour éviter de saturer MongoDB (Timeout/Erreur 500), on fait les requêtes séquentiellement
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const billsRes = await axios.get(`/api/BillsValises/getAllBillsV?slim=true&startDate=${startDate}&endDate=${endDate}`, config);
      const productsRes = await axios.get("/api/ProductV/getProductV?slim=true", config);
      const invoicesRes = await axios.get("/api/InvoicesV/getInvoices?slim=true", config);
      const locationsRes = await axios.get("/api/Locations/getAllLocations", config);

      const bills = billsRes.data || [];
      const products = productsRes.data || [];
      const invoices = invoicesRes.data || [];
      const locations = locationsRes.data || [];

      const extractLocId = (loc) => {
        if (!loc) return null;
        if (typeof loc === "string") return loc;
        if (loc.$oid) return loc.$oid;
        if (loc._id) {
          if (typeof loc._id === 'string') return loc._id;
          if (loc._id.$oid) return loc._id.$oid;
          return loc._id.toString();
        }
        return loc.toString();
      };

      const locMap = new Map();
      locations.forEach(loc => {
        const nameLower = (loc.name || "").toLowerCase();
        if (nameLower.includes("family")) return; // Éliminer Family Park
        const idStr = extractLocId(loc._id || loc);
        if (idStr) {
          locMap.set(idStr, nameLower);
        }
      });

      // 1. Purchase Prices
      const purchasePriceMap = new Map();
      for (const invoice of invoices) {
        if (!invoice.items || !Array.isArray(invoice.items)) continue;
        for (const item of invoice.items) {
          let purchasePriceTTC = (item.pricettc && item.pricettc > 0) ? item.pricettc : (item.price || 0) * (1 + (item.tva || 0) / 100);
          const itemProductId = item.productId?.toString() || item.productV?.toString() || item.productV?._id?.toString();
          const itemSubProductId = item.subProductId?.toString() || item.subProductV?.toString() || item.subProductV?._id?.toString();

          if (itemSubProductId) purchasePriceMap.set(`sub_${itemSubProductId}`, purchasePriceTTC);
          if (itemProductId && !purchasePriceMap.has(`prod_${itemProductId}`)) purchasePriceMap.set(`prod_${itemProductId}`, purchasePriceTTC);
        }
      }

      // 2. Products
      const productMap = new Map();
      for (const product of products) {
        productMap.set(`prod_${product._id}`, { ...product, isSub: false });
        if (product.subProductsV && Array.isArray(product.subProductsV)) {
          for (const sub of product.subProductsV) productMap.set(`sub_${sub._id}`, { ...sub, parentId: product._id, isSub: true });
        }
      }

      const findPurchasePrice = (productId, subProductId) => {
        if (subProductId && purchasePriceMap.has(`sub_${subProductId}`)) return purchasePriceMap.get(`sub_${subProductId}`);
        if (productId && purchasePriceMap.has(`prod_${productId}`)) return purchasePriceMap.get(`prod_${productId}`);
        return 0;
      };

      // 3. Process Bills for Twin Toys
      const result = {};
      bills.forEach(bill => {
        if (bill.status === "cancelled") return;
        
        // Exclure les ventes réalisées par les caisses des autres entités (E-space, Bagn'Go, Spintoss, Wellness)
        if (bill.user) {
          const uRole = (bill.user.role || "").toLowerCase();
          const uName = (bill.user.name || "").toLowerCase();
          const isOtherEntity = 
            uRole === "userespace" || uName.includes("espace") || uName.includes("e-space") ||
            uRole === "userbagngo" || uName.includes("bagngo") || uName.includes("bng") ||
            uRole === "userspintoss" || uName.includes("spintoss") || uName.includes("spin") ||
            uRole === "userwellness";
            
          if (isOtherEntity) return;
        }

        let locName = "";
        if (bill.localisation) {
          const locId = extractLocId(bill.localisation);
          locName = locMap.get(locId) || "";
        }

        // Fallback sur la localisation de l'utilisateur si la localisation de la facture est manquante
        if (!locName && bill.user && bill.user.localisation) {
          locName = bill.user.localisation.toLowerCase();
        }
        
        let lKey = "Inconnu";
        if (locName.includes("manar") && !locName.includes("bagngo") && !locName.includes("espace")) lKey = "Manarcity";
        else if ((locName.includes("tunisia mall") || locName.includes("tunisiamall")) && !locName.includes("bagngo") && !locName.includes("espace")) lKey = "Tunisiamall";
        else if (locName.includes("menzah") && !locName.includes("bagngo") && !locName.includes("espace")) lKey = "Lesjardinsdelmenzah";
        
        // Si lKey est toujours "Inconnu", ce n'est pas un emplacement Twin Toys
        if (lKey === "Inconnu") return;
        
        if (!result[lKey]) result[lKey] = { posCA_toys: 0, cost_toys: 0, posCA_candy: 0, cost_candy: 0 };

        if (bill.items && Array.isArray(bill.items)) {
          bill.items.forEach(item => {
            if (item.isCancelled) return;
            const quantity = item.quantity || 1;
            
            let purchasePriceTTC = 0;
            let category = "Autre";
            let fallbackPrice = 0;
            const itemId = item.id?.toString();
            const prodMatch = productMap.get(`prod_${itemId}`);
            const subMatch = productMap.get(`sub_${itemId}`);

            if (prodMatch && !prodMatch.isSub) {
              category = prodMatch.category;
              fallbackPrice = prodMatch.price || 0;
              if (prodMatch.category === "SET" && prodMatch.subProductsV?.length > 0) {
                purchasePriceTTC = prodMatch.subProductsV.reduce((total, subProduct) => total + findPurchasePrice(prodMatch._id, subProduct._id), 0);
              } else {
                purchasePriceTTC = findPurchasePrice(prodMatch._id, null);
              }
            } else if (subMatch && subMatch.isSub) {
              const parentProd = productMap.get(`prod_${subMatch.parentId}`);
              category = parentProd ? parentProd.category : "Autre";
              fallbackPrice = subMatch.price || 0;
              purchasePriceTTC = findPurchasePrice(subMatch.parentId, subMatch._id);
            }

            // Marge.js inclut TOUT ce qui est dans BillValises sans exception
            const sellingPrice = item.price !== undefined ? item.price : fallbackPrice;
            const salesAmount = sellingPrice * quantity;
            
            const catStr = (category || "").toString().toUpperCase().trim();
            const isCandy = catStr.includes("CANDY") || catStr === "CONFISERIE";

            if (isCandy) {
              result[lKey].posCA_candy += salesAmount;
              result[lKey].cost_candy += purchasePriceTTC * quantity;
            } else {
              result[lKey].posCA_toys += salesAmount;
              result[lKey].cost_toys += purchasePriceTTC * quantity;
            }
          });
        }
      });

      setTwinToysPosData(result);
    } catch (err) {
      console.error("Error exact TwinToys cost:", err);
    }
  }, [startDate, endDate, token, summaryData]); // <-- on ajoute summaryData comme dépendance

  useEffect(() => {
    // Ne lancer le calcul que SI l'analytique globale a terminé (évite l'engorgement de MongoDB)
    if (summaryData) {
      fetchTwinToysPosAndCost();
    }
  }, [fetchTwinToysPosAndCost, summaryData]);

  const twinparkLocations = [
    "Twin Park- Manar City",
    "Twin Park - Tunisia Mall",
    "Twin park - Jardins d'eL Menzah",
    "Jump Zone Mall of Djerba"
  ];

  // Map user-facing strings to keys used in DB/posSalesByEntity
  const getLocKey = (locName) => {
    if (!locName) return "Inconnu";
    const l = locName.toLowerCase();
    if (l.includes("manar")) return "Manarcity";
    if (l.includes("tunisia mall") || l.includes("tunisiamall")) return "Tunisiamall";
    if (l.includes("menzah") || l.includes("lesjardinsdelmenzah")) return "Lesjardinsdelmenzah";
    if (l.includes("jump") || l.includes("djerba")) return "Jumpzone";
    return locName.replace(/\s/g, '');
  };

  // Filter Data (Basic Rapports Twin)
  const filteredData = useMemo(() => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    return rapports.filter((entry) => {
      // Éliminer Family Park
      if (entry.localisation && entry.localisation.toLowerCase().includes("family")) {
        return false;
      }
      if (localisation !== "All" && localisation !== "E-space" && entry.localisation !== localisation) {
        return false;
      }
      const entryDate = new Date(entry.DateCollect);
      const isDateInRange = (!start || entryDate >= start) && (!end || entryDate <= end);
      return isDateInRange;
    }).sort((a, b) => new Date(b.DateCollect) - new Date(a.DateCollect));
  }, [rapports, localisation, startDate, endDate]);

  // Filter Charge d'exploitation
  const filteredCharges = useMemo(() => {
    const start = startDate ? new Date(startDate).getTime() : null;
    const end = endDate ? new Date(endDate).getTime() : null;
    
    return chargesExp.filter((entry) => {
      // Éliminer Family Park des charges d'exploitation
      if (entry.localisation && entry.localisation.toLowerCase().includes("family")) {
        return false;
      }
      const entryDate = new Date(entry.DateCollect || entry.createdAt).getTime();
      return (!start || entryDate >= start) && (!end || entryDate <= end);
    });
  }, [chargesExp, startDate, endDate]);

  // Aggregate AchatsToys from ChargeExp by location
  const costsByLocation = useMemo(() => {
    const map = {};
    filteredCharges.forEach(charge => {
      const locKey = getLocKey(charge.localisation);
      if (charge.AchatsToys) {
        map[locKey] = (map[locKey] || 0) + charge.AchatsToys;
      }
    });
    return map;
  }, [filteredCharges]);

  // KPIs Calculation from Rapports
  const kpis = useMemo(() => {
    let totalCA = 0; let totalTPE = 0; let totalEspeces = 0; let totalDepenses = 0;
    let totalPlayzone = 0; let totalTwinToys = 0; let totalAutresRubriques = 0;
    let totalDailyCA = 0; let totalCollecteCA = 0;

    filteredData.forEach(item => {
      totalCA += item.TotalAmount || 0;
      totalTPE += item.TotalTpe || 0;
      totalDepenses += item.DailyExpenses || 0;
      
      const isCollecte = item.objet && item.objet.trim() !== "";
      if (isCollecte) {
        totalCollecteCA += item.TotalAmount || 0;
      } else {
        totalDailyCA += item.TotalAmount || 0;
        totalPlayzone += (item.PlayZoneStandard || 0) + (item.PlayZoneSurveillé || 0) + (item.AccesParent || 0);
        totalTwinToys += item.TwinToys || 0;
        const autres = (item.PackQR || 0) + (item.RechargeQR || 0) + (item.PackCard || 0) + 
                       (item.RechargeCard || 0) + (item.TwinClub || 0) + (item.ChallengeGames || 0) + 
                       (item.CandyBar || 0) + (item.PackAnnif || 0);
        totalAutresRubriques += autres;
      }
    });

    totalEspeces = totalCA - totalTPE;
    const resultatNet = totalCA - totalDepenses;

    return { 
      totalCA, 
      totalTPE, 
      totalEspeces, 
      totalDepenses, 
      resultatNet, 
      totalPlayzone, 
      totalTwinToys, 
      totalAutresRubriques,
      totalDailyCA,
      totalCollecteCA
    };
  }, [filteredData]);

  // Breakdown Reports data by Location
  const reportsByLocation = useMemo(() => {
    const map = {};
    twinparkLocations.forEach(loc => map[getLocKey(loc)] = { 
      rapCA: 0, 
      dailyCA: 0, 
      collecteCA: 0, 
      twinToysCA: 0, 
      expenses: 0, 
      tpe: 0,
      locName: loc 
    });
    
    filteredData.forEach(item => {
      const lKey = getLocKey(item.localisation);
      if (!map[lKey]) {
        map[lKey] = { 
          rapCA: 0, 
          dailyCA: 0, 
          collecteCA: 0, 
          twinToysCA: 0, 
          expenses: 0, 
          tpe: 0,
          locName: item.localisation 
        };
      }
      
      const isCollecte = item.objet && item.objet.trim() !== "";
      
      if (isCollecte) {
        map[lKey].collecteCA += item.TotalAmount || 0;
      } else {
        map[lKey].dailyCA += item.TotalAmount || 0;
        map[lKey].twinToysCA += item.TwinToys || 0;
      }
      
      map[lKey].rapCA += item.TotalAmount || 0;
      map[lKey].expenses += item.DailyExpenses || 0;
      map[lKey].tpe += item.TotalTpe || 0;
    });
    return map;
  }, [filteredData, twinparkLocations]);

  // Aggregate Machine Collections by Location
  const machinesByLocation = useMemo(() => {
    const map = {};
    twinparkLocations.forEach(loc => {
      map[getLocKey(loc)] = {
        locName: loc,
        boxe: 0,
        foot: 0,
        boxeEnfant: 0,
        billard: 0,
        babyfoot: 0,
        ballesGift: 0,
        cinema: 0,
        others: 0,
        total: 0
      };
    });

    filteredData.forEach(item => {
      const isCollecte = item.objet && item.objet.trim() !== "";
      if (!isCollecte) return;

      const lKey = getLocKey(item.localisation);
      if (!map[lKey]) {
        map[lKey] = {
          locName: item.localisation,
          boxe: 0,
          foot: 0,
          boxeEnfant: 0,
          billard: 0,
          babyfoot: 0,
          ballesGift: 0,
          cinema: 0,
          others: 0,
          total: 0
        };
      }

      const boxe = item.MachineBoxe || 0;
      const foot = item.MachineFoot || 0;
      const boxeEnfant = item.MachineBoxeEnfant || 0;
      const billard = item.BillardAmericain || 0;
      const babyfoot = item.BabyFoot || 0;
      const ballesGift = item.BallesGift || 0;
      const cinema = item.Cinema || 0;
      
      const knownSum = boxe + foot + boxeEnfant + billard + babyfoot + ballesGift + cinema;
      const others = Math.max(0, (item.TotalAmount || 0) - knownSum);

      map[lKey].boxe += boxe;
      map[lKey].foot += foot;
      map[lKey].boxeEnfant += boxeEnfant;
      map[lKey].billard += billard;
      map[lKey].babyfoot += babyfoot;
      map[lKey].ballesGift += ballesGift;
      map[lKey].cinema += cinema;
      map[lKey].others += others;
      map[lKey].total += item.TotalAmount || 0;
    });

    return map;
  }, [filteredData, twinparkLocations]);

  // Data for Charts: CA by Location
  const caByLocation = useMemo(() => {
    const res = [];
    twinparkLocations.forEach(loc => {
      if (localisation === "All" || loc === localisation) {
        res.push({
          loc: loc.replace('Twin Park', 'TP').replace('Twin park', 'TP').replace('Jump Zone Mall of Djerba', 'Djerba'),
          CA: reportsByLocation[getLocKey(loc)]?.rapCA || 0
        });
      }
    });
    if (summaryData && (localisation === "All" || localisation === "E-space")) {
      res.push({
        loc: "E-space",
        CA: summaryData.entities.Espace.rapCA || summaryData.entities.Espace.posCA || 0
      });
    }
    return res;
  }, [summaryData, localisation, reportsByLocation, twinparkLocations]);

  const totalRubriques = kpis.totalPlayzone + kpis.totalTwinToys + kpis.totalAutresRubriques;
  
  const rubriquesPieData = useMemo(() => [
    { id: 0, value: kpis.totalPlayzone, label: `Playzone (${getPercentage(kpis.totalPlayzone, totalRubriques)})`, color: '#673ab7' },
    { id: 1, value: kpis.totalTwinToys, label: `Twin Toys (${getPercentage(kpis.totalTwinToys, totalRubriques)})`, color: '#e91e63' },
    { id: 2, value: kpis.totalAutresRubriques, label: `Autres (${getPercentage(kpis.totalAutresRubriques, totalRubriques)})`, color: '#ff9800' },
  ].filter(item => item.value > 0), [kpis.totalPlayzone, kpis.totalTwinToys, kpis.totalAutresRubriques, totalRubriques]);

  const paymentPieData = useMemo(() => [
    { id: 0, value: kpis.totalEspeces, label: `Espèces (${getPercentage(kpis.totalEspeces, kpis.totalCA)})`, color: '#4caf50' },
    { id: 1, value: kpis.totalTPE, label: `TPE (${getPercentage(kpis.totalTPE, kpis.totalCA)})`, color: '#2196f3' }
  ].filter(item => item.value > 0), [kpis.totalEspeces, kpis.totalTPE, kpis.totalCA]);

  const tpeByLocationData = useMemo(() => {
    const data = [];
    let idCounter = 0;
    const colors = ['#673ab7', '#e91e63', '#ff9800', '#2196f3', '#009688', '#9c27b0'];
    
    twinparkLocations.forEach(loc => {
      const lKey = getLocKey(loc);
      const locTpe = reportsByLocation[lKey]?.tpe || 0;
      if (locTpe > 0) {
        data.push({
          id: idCounter++,
          value: locTpe,
          label: `${loc.replace('Twin Park - ', '').replace('Twin Park- ', '').replace('Twin park - ', '').replace('Jump Zone Mall of Djerba', 'Djerba')} (${getPercentage(locTpe, kpis.totalTPE)})`,
          color: colors[idCounter % colors.length]
        });
      }
    });
    
    return data;
  }, [reportsByLocation, twinparkLocations, kpis.totalTPE]);

  // Expenses By Location
  const expensesByLocation = useMemo(() => {
    const map = {};
    filteredData.forEach(item => {
      if (!map[item.localisation]) {
        map[item.localisation] = { loc: item.localisation, taxi: 0, repair: 0, advance: 0, misc: 0, other: 0, total: 0 };
      }
      map[item.localisation].taxi += item.TaxiExpenses || 0;
      map[item.localisation].repair += item.RepairExpenses || 0;
      map[item.localisation].advance += item.StaffAdvance || 0;
      map[item.localisation].misc += item.MiscPurchases || 0;
      map[item.localisation].other += item.OtherExpenses || 0;
      map[item.localisation].total += item.DailyExpenses || 0;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredData]);



  return (
    <div className="twinpark-dashboard-container">
      <div className="twinpark-header">
        <h1>Statistiques Twinpark & Audit</h1>
        <p>Analyse détaillée par parc, Marges et Comparatif POS vs Rapports</p>
      </div>

      <div className="twinpark-filters">
        <div className="filter-group">
          <label>Emplacement</label>
          <select value={localisation} onChange={(e) => setLocalisation(e.target.value)}>
            <option value="All">Tous les parcs (Inclus E-space)</option>
            {twinparkLocations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
            <option value="E-space">E-space</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Date de début</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Date de fin</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {loading && <p style={{textAlign: 'center', color: '#666'}}>Chargement des données croisées (POS / Marges)...</p>}

      {/* KPIs Principaux */}
      <div className="twinpark-kpis">
        <div className="kpi-card">
          <div className="kpi-title">CA Déclaré (Rapports)</div>
          <div className="kpi-value primary">{formatCurrency(kpis.totalCA)}</div>
          <small style={{color: '#666'}}>
            Daily: {formatCurrency(kpis.totalDailyCA)} | Collectes: {formatCurrency(kpis.totalCollecteCA)}
          </small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Espèces (Cash)</div>
          <div className="kpi-value info">{formatCurrency(kpis.totalEspeces)}</div>
          <small style={{color: '#888'}}>Soit {getPercentage(kpis.totalEspeces, kpis.totalCA)} du CA</small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Paiements TPE</div>
          <div className="kpi-value info">{formatCurrency(kpis.totalTPE)}</div>
          <small style={{color: '#888'}}>Soit {getPercentage(kpis.totalTPE, kpis.totalCA)} du CA</small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Dépenses Déclarées</div>
          <div className="kpi-value warning">{formatCurrency(kpis.totalDepenses)}</div>
          <small style={{color: '#F44336', fontWeight: 'bold'}}>Ratio: {getPercentage(kpis.totalDepenses, kpis.totalCA)} du CA</small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Résultat Net Déclaré</div>
          <div className={`kpi-value ${kpis.resultatNet >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(kpis.resultatNet)}
          </div>
        </div>
      </div>

      {/* Section des Graphiques Analytiques */}
      <div className="twinpark-charts">
        <div className="chart-container">
          <div className="chart-title">Répartition par Rubrique (Déclaré)</div>
          <PieChart
            series={[
              {
                data: rubriquesPieData,
                innerRadius: 35,
                outerRadius: 90,
                paddingAngle: 4,
                cornerRadius: 4,
              },
            ]}
            width={380}
            height={240}
            slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' } } }}
          />
        </div>

        <div className="chart-container">
          <div className="chart-title">Mode de Paiement (Cash vs TPE)</div>
          <PieChart
            series={[
              {
                data: paymentPieData,
                innerRadius: 35,
                outerRadius: 90,
                paddingAngle: 4,
                cornerRadius: 4,
              },
            ]}
            width={380}
            height={240}
            slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' } } }}
          />
        </div>

        <div className="chart-container">
          <div className="chart-title">Répartition TPE par Parc</div>
          {tpeByLocationData.length > 0 ? (
            <PieChart
              series={[
                {
                  data: tpeByLocationData,
                  innerRadius: 35,
                  outerRadius: 90,
                  paddingAngle: 4,
                  cornerRadius: 4,
                },
              ]}
              width={380}
              height={240}
              slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' } } }}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#888', fontStyle: 'italic' }}>Aucune donnée TPE pour cette période.</p>
          )}
        </div>

        <div className="chart-container">
          <div className="chart-title">CA Déclaré par Emplacement</div>
          <BarChart
            dataset={caByLocation}
            xAxis={[{ scaleType: 'band', dataKey: 'loc' }]}
            series={[{ dataKey: 'CA', color: '#2196F3' }]}
            width={450}
            height={260}
          />
        </div>
      </div>

      {summaryData && (
        <>
          <div className="twinpark-table-container">
            <h3 style={{marginBottom: '20px', color: '#333'}}>🧸 Audit Twin Toys (Comparatif POS vs Rapports)</h3>
            <div className="table-responsive">
              <table className="twinpark-table">
                <thead>
                  <tr>
                    <th>Emplacement</th>
                    <th>Rapports Twin Toys CA</th>
                    <th>CA Réel Jouets (POS)</th>
                    <th>Écart (Jouets)</th>
                    <th>Coût d'Achat Jouets</th>
                    <th>Marge Jouets</th>
                  </tr>
                </thead>
                <tbody>
                  {twinparkLocations.map((locName) => {
                    if (localisation !== "All" && locName.toLowerCase() !== localisation.toLowerCase()) return null;
                    
                    const lKey = getLocKey(locName);
                    const rapCA = reportsByLocation[lKey]?.twinToysCA || 0;
                    const posCA_toys = twinToysPosData[lKey]?.posCA_toys || 0;
                    const diff = posCA_toys - rapCA;
                    const cost_toys = twinToysPosData[lKey]?.cost_toys || 0;
                    const margin_toys = posCA_toys - cost_toys;

                    return (
                      <tr key={locName}>
                        <td>{locName}</td>
                        <td>{formatCurrency(rapCA)}</td>
                        <td>{formatCurrency(posCA_toys)}</td>
                        <td style={{color: diff < 0 ? '#F44336' : '#4CAF50'}}>{formatCurrency(diff)}</td>
                        <td style={{color: '#F44336'}}>{formatCurrency(cost_toys)}</td>
                        <td style={{color: margin_toys < 0 ? '#F44336' : '#4CAF50', fontWeight: 'bold'}}>{formatCurrency(margin_toys)}</td>
                      </tr>
                    );
                  })}

                  {/* Ligne Globale Twin Toys */}
                  {(localisation === "All") && (() => {
                    const totalPosCAToys = Object.values(twinToysPosData).reduce((a,b)=>a+(b.posCA_toys||0), 0);
                    const totalCostToys = Object.values(twinToysPosData).reduce((a,b)=>a+(b.cost_toys||0), 0);
                    
                    const diff = totalPosCAToys - summaryData.entities.TwinToys.rapCA;
                    return (
                    <tr style={{backgroundColor: '#f1f5f9', fontWeight: 'bold'}}>
                      <td>TOTAL TWIN TOYS</td>
                      <td>{formatCurrency(summaryData.entities.TwinToys.rapCA)}</td>
                      <td>{formatCurrency(totalPosCAToys)}</td>
                      <td style={{color: diff < 0 ? '#F44336' : '#4CAF50'}}>
                        {formatCurrency(diff)}
                      </td>
                      <td style={{color: '#F44336'}}>
                        {formatCurrency(totalCostToys)}
                      </td>
                      <td style={{color: '#4CAF50'}}>
                        {formatCurrency(totalPosCAToys - totalCostToys)}
                      </td>
                    </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {/* Audit Global Candy Bar (Table Indépendante) */}
            <div className="twinpark-table-container" style={{marginTop: '40px', padding: 0, boxShadow: 'none'}}>
              <h3 style={{marginBottom: '20px', color: '#333'}}>🍭 Audit Global Candy Bar</h3>
              <div className="table-responsive">
                <table className="twinpark-table">
                  <thead>
                    <tr>
                      <th>Emplacement</th>
                      <th>CA Réel Candy Bar (POS)</th>
                      <th>Coût d'Achat Candy Bar</th>
                      <th>Marge Nette Candy Bar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {twinparkLocations.map((locName) => {
                      if (localisation !== "All" && locName.toLowerCase() !== localisation.toLowerCase()) return null;
                      
                      const lKey = getLocKey(locName);
                      const posCA_candy = twinToysPosData[lKey]?.posCA_candy || 0;
                      const cost_candy = twinToysPosData[lKey]?.cost_candy || 0;
                      const margin_candy = posCA_candy - cost_candy;

                      return (
                        <tr key={locName}>
                          <td>{locName}</td>
                          <td>{formatCurrency(posCA_candy)}</td>
                          <td style={{color: '#F44336'}}>{formatCurrency(cost_candy)}</td>
                          <td style={{color: margin_candy < 0 ? '#F44336' : '#4CAF50', fontWeight: 'bold'}}>{formatCurrency(margin_candy)}</td>
                        </tr>
                      );
                    })}

                    {/* Ligne Globale Candy Bar */}
                    {(localisation === "All") && (() => {
                      const totalPosCACandy = Object.values(twinToysPosData).reduce((a,b)=>a+(b.posCA_candy||0), 0);
                      const totalCostCandy = Object.values(twinToysPosData).reduce((a,b)=>a+(b.cost_candy||0), 0);
                      const totalMarginCandy = totalPosCACandy - totalCostCandy;
                      return (
                      <tr style={{backgroundColor: '#fdf2f8', fontWeight: 'bold'}}>
                        <td>TOTAL CANDY BAR</td>
                        <td>{formatCurrency(totalPosCACandy)}</td>
                        <td style={{color: '#F44336'}}>{formatCurrency(totalCostCandy)}</td>
                        <td style={{color: '#4CAF50'}}>{formatCurrency(totalMarginCandy)}</td>
                      </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
 
          {/* Table récapitulative des collectes de machines */}
          <div className="twinpark-table-container" style={{marginTop: '40px'}}>
            <h3 style={{marginBottom: '20px', color: '#333'}}>🎰 Détail des Collectes de Machines</h3>
            <div className="table-responsive">
              <table className="twinpark-table">
                <thead>
                  <tr>
                    <th>Emplacement</th>
                    <th>Boxe</th>
                    <th>Foot</th>
                    <th>Boxe Enfant</th>
                    <th>Billard</th>
                    <th>Babyfoot</th>
                    <th>Balles Gift</th>
                    <th>Cinéma 10D</th>
                    <th>Autres Collectes</th>
                    <th style={{color: '#2196F3'}}>Total Collectes</th>
                  </tr>
                </thead>
                <tbody>
                  {twinparkLocations.map((locName) => {
                    if (localisation !== "All" && locName.toLowerCase() !== localisation.toLowerCase()) return null;
                    
                    const lKey = getLocKey(locName);
                    const machineData = machinesByLocation[lKey] || {
                      boxe: 0, foot: 0, boxeEnfant: 0, billard: 0, babyfoot: 0, ballesGift: 0, cinema: 0, others: 0, total: 0
                    };
 
                    return (
                      <tr key={locName}>
                        <td>{locName}</td>
                        <td>{formatCurrency(machineData.boxe)}</td>
                        <td>{formatCurrency(machineData.foot)}</td>
                        <td>{formatCurrency(machineData.boxeEnfant)}</td>
                        <td>{formatCurrency(machineData.billard)}</td>
                        <td>{formatCurrency(machineData.babyfoot)}</td>
                        <td>{formatCurrency(machineData.ballesGift)}</td>
                        <td>{formatCurrency(machineData.cinema)}</td>
                        <td>{formatCurrency(machineData.others)}</td>
                        <td style={{fontWeight: 'bold', color: '#2196F3'}}>{formatCurrency(machineData.total)}</td>
                      </tr>
                    );
                  })}
 
                  {/* Ligne Globale Collectes */}
                  {(localisation === "All") && (() => {
                    const totalBoxe = Object.values(machinesByLocation).reduce((a,b)=>a+(b.boxe||0), 0);
                    const totalFoot = Object.values(machinesByLocation).reduce((a,b)=>a+(b.foot||0), 0);
                    const totalBoxeEnfant = Object.values(machinesByLocation).reduce((a,b)=>a+(b.boxeEnfant||0), 0);
                    const totalBillard = Object.values(machinesByLocation).reduce((a,b)=>a+(b.billard||0), 0);
                    const totalBabyfoot = Object.values(machinesByLocation).reduce((a,b)=>a+(b.babyfoot||0), 0);
                    const totalBalles = Object.values(machinesByLocation).reduce((a,b)=>a+(b.ballesGift||0), 0);
                    const totalCinema = Object.values(machinesByLocation).reduce((a,b)=>a+(b.cinema||0), 0);
                    const totalOthers = Object.values(machinesByLocation).reduce((a,b)=>a+(b.others||0), 0);
                    const grandTotal = Object.values(machinesByLocation).reduce((a,b)=>a+(b.total||0), 0);
                    
                    return (
                      <tr style={{backgroundColor: '#f1f5f9', fontWeight: 'bold'}}>
                        <td>TOTAL CUMULÉ</td>
                        <td>{formatCurrency(totalBoxe)}</td>
                        <td>{formatCurrency(totalFoot)}</td>
                        <td>{formatCurrency(totalBoxeEnfant)}</td>
                        <td>{formatCurrency(totalBillard)}</td>
                        <td>{formatCurrency(totalBabyfoot)}</td>
                        <td>{formatCurrency(totalBalles)}</td>
                        <td>{formatCurrency(totalCinema)}</td>
                        <td>{formatCurrency(totalOthers)}</td>
                        <td style={{color: '#2196F3'}}>{formatCurrency(grandTotal)}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
 
          {/* Synthèse par Parc (Incluant E-Space) */}
          <div className="twinpark-table-container">
            <h3 style={{marginBottom: '20px', color: '#333'}}>🏢 Rentabilité & CA par Parc (Audit Global)</h3>
            <table className="twinpark-table">
              <thead>
                <tr>
                  <th>Parc / Entité</th>
                  <th>CA Réel (Caisse POS)</th>
                  <th>CA Tickets/Playzone (Daily)</th>
                  <th>CA Collectes (Machines)</th>
                  <th>CA Déclaré Total</th>
                  <th>Dépenses (Rapports)</th>
                  <th>Ratio (Dépenses/CA)</th>
                  <th style={{color: '#4CAF50'}}>Bénéfice Net</th>
                </tr>
              </thead>
              <tbody>
                {/* Lignes pour chaque Twin Park si All ou le parc est sélectionné */}
                {twinparkLocations.map(locName => {
                  if (localisation !== "All" && locName !== localisation) return null;
                  
                  const lKey = getLocKey(locName);
                  const data = reportsByLocation[lKey] || {};
                  
                  // CA Tickets/Playzone = dailyCA - twinToysCA (on exclut les jouets de la playzone standard)
                  const dailyCA_Parc = (data.dailyCA || 0) - (data.twinToysCA || 0);
                  const collecteCA_Parc = data.collecteCA || 0;
                  const rapCA_Parc = dailyCA_Parc + collecteCA_Parc;
                  
                  const posCA_Parc = summaryData.entities.TwinPark.locations?.[lKey] || 0;
                  
                  // Chiffre d'affaires total à prendre en compte pour la rentabilité :
                  // Max entre POS et Rapports Journaliers (car ce sont les mêmes caisses), plus les collectes de machines (qui sont à part)
                  const maxCA = Math.max(posCA_Parc, dailyCA_Parc) + collecteCA_Parc;
                  const exp = data.expenses || 0;
                  const net = maxCA - exp;
                  const ratio = maxCA > 0 ? ((exp / maxCA) * 100).toFixed(1) + '%' : '0%';
 
                  return (
                    <tr key={locName}>
                      <td style={{fontWeight: 'bold', color: '#333'}}>{locName} (Hors Toys)</td>
                      <td>{formatCurrency(posCA_Parc)}</td>
                      <td>{formatCurrency(dailyCA_Parc)}</td>
                      <td>{formatCurrency(collecteCA_Parc)}</td>
                      <td style={{fontWeight: 'bold'}}>{formatCurrency(rapCA_Parc)}</td>
                      <td style={{color: '#F44336'}}>{formatCurrency(exp)}</td>
                      <td style={{fontWeight: 'bold', color: '#ff9800'}}>{ratio}</td>
                      <td style={{color: net >= 0 ? '#4CAF50' : '#F44336', fontWeight: 'bold'}}>{formatCurrency(net)}</td>
                    </tr>
                  );
                })}
 
                {/* E-space */}
                {(localisation === "All" || localisation === "E-space") && (
                  <tr style={{backgroundColor: '#f8fafc'}}>
                    <td style={{fontWeight: 'bold', color: '#0ea5e9'}}>E-space</td>
                    <td>{formatCurrency(summaryData.entities.Espace.posCA)}</td>
                    <td colSpan="3" style={{textAlign: 'center', color: '#888', fontStyle: 'italic'}}>
                      CA Déclaré: {formatCurrency(summaryData.entities.Espace.rapCA)}
                    </td>
                    <td style={{color: '#F44336'}}>{formatCurrency(summaryData.entities.Espace.expenses)}</td>
                    <td style={{fontWeight: 'bold', color: '#ff9800'}}>
                      {summaryData.entities.Espace.rapCA > 0 ? ((summaryData.entities.Espace.expenses / summaryData.entities.Espace.rapCA) * 100).toFixed(1) + '%' : '0%'}
                    </td>
                    <td style={{color: (Math.max(summaryData.entities.Espace.posCA, summaryData.entities.Espace.rapCA) - summaryData.entities.Espace.expenses) >= 0 ? '#4CAF50' : '#F44336', fontWeight: 'bold'}}>
                      {formatCurrency(Math.max(summaryData.entities.Espace.posCA, summaryData.entities.Espace.rapCA) - summaryData.entities.Espace.expenses)}
                    </td>
                  </tr>
                )}
 
                {/* TOTAL CUMULÉ SI 'ALL' */}
                {localisation === "All" && (
                  <tr style={{backgroundColor: '#e2e8f0', fontWeight: 'bold'}}>
                    <td>TOTAL (Twinpark + E-space)</td>
                    <td>{formatCurrency(summaryData.entities.TwinPark.posCA + summaryData.entities.Espace.posCA)}</td>
                    <td colSpan="3" style={{textAlign: 'center', color: '#666', fontStyle: 'italic'}}>
                      CA Déclaré: {formatCurrency(summaryData.entities.TwinPark.rapCA + summaryData.entities.Espace.rapCA)}
                    </td>
                    <td style={{color: '#F44336'}}>{formatCurrency(summaryData.entities.TwinPark.expenses + summaryData.entities.Espace.expenses)}</td>
                    <td style={{color: '#ff9800'}}>
                      {((summaryData.entities.TwinPark.expenses + summaryData.entities.Espace.expenses) / (Math.max(summaryData.entities.TwinPark.posCA, summaryData.entities.TwinPark.rapCA) + Math.max(summaryData.entities.Espace.posCA, summaryData.entities.Espace.rapCA)) * 100 || 0).toFixed(1)}%
                    </td>
                    <td style={{color: '#4CAF50'}}>
                      {formatCurrency(
                        (Math.max(summaryData.entities.TwinPark.posCA, summaryData.entities.TwinPark.rapCA) - summaryData.entities.TwinPark.expenses) +
                        (Math.max(summaryData.entities.Espace.posCA, summaryData.entities.Espace.rapCA) - summaryData.entities.Espace.expenses)
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}



      {/* Détails des dépenses par parc */}
      <div className="twinpark-table-container">
        <h3 style={{marginBottom: '20px', color: '#333'}}>💸 Détail des Dépenses par Parc</h3>
        {expensesByLocation.length > 0 ? (
          <table className="twinpark-table">
            <thead>
              <tr>
                <th>Emplacement</th>
                <th>Taxi</th>
                <th>Réparation</th>
                <th>Avance Perso</th>
                <th>Achats Divers</th>
                <th>Autres</th>
                <th style={{color: '#F44336'}}>Total Dépenses</th>
              </tr>
            </thead>
            <tbody>
              {expensesByLocation.map((item, index) => (
                <tr key={index}>
                  <td style={{fontWeight: 'bold'}}>{item.loc}</td>
                  <td>{formatCurrency(item.taxi)}</td>
                  <td>{formatCurrency(item.repair)}</td>
                  <td>{formatCurrency(item.advance)}</td>
                  <td>{formatCurrency(item.misc)}</td>
                  <td>{formatCurrency(item.other)}</td>
                  <td style={{color: '#F44336', fontWeight: 'bold'}}>{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{textAlign: 'center', color: '#777', padding: '20px'}}>Aucune dépense détaillée trouvée pour ces critères.</p>
        )}
      </div>

      {/* Détail des Rapports Journaliers */}
      <div className="twinpark-table-container">
        <h3 style={{marginBottom: '20px', color: '#333'}}>📋 Détail des Rapports Journaliers</h3>
        {filteredData.length > 0 ? (
          <div style={{overflowX: 'auto'}}>
            <table className="twinpark-table" style={{minWidth: '1200px'}}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Emplacement</th>
                  <th>PlayZone (Std)</th>
                  <th>PlayZone (Surv)</th>
                  <th>Accès Parent</th>
                  <th>Pack QR</th>
                  <th>Rech. QR</th>
                  <th>Pack Card</th>
                  <th>Rech. Card</th>
                  <th>Twin Toys</th>
                  <th>Anniversaire</th>
                  <th style={{color: '#F44336'}}>Dépenses</th>
                  <th style={{color: '#4CAF50'}}>Total CA</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const map = {};
                  filteredData.forEach(item => {
                    // Exclure les collectes du tableau récapitulatif des rapports journaliers
                    const isCollecte = item.objet && item.objet.trim() !== "";
                    if (isCollecte) return;

                    const loc = item.localisation || "Inconnu";
                    if (!map[loc]) {
                      map[loc] = {
                        localisation: loc,
                        PlayZoneStandard: 0,
                        PlayZoneSurveillé: 0,
                        AccesParent: 0,
                        PackQR: 0,
                        RechargeQR: 0,
                        PackCard: 0,
                        RechargeCard: 0,
                        TwinToys: 0,
                        PackAnnif: 0,
                        DailyExpenses: 0,
                        TotalAmount: 0
                      };
                    }
                    map[loc].PlayZoneStandard += (item.PlayZoneStandard || 0);
                    map[loc].PlayZoneSurveillé += (item.PlayZoneSurveillé || 0);
                    map[loc].AccesParent += (item.AccesParent || 0);
                    map[loc].PackQR += (item.PackQR || 0);
                    map[loc].RechargeQR += (item.RechargeQR || 0);
                    map[loc].PackCard += (item.PackCard || 0);
                    map[loc].RechargeCard += (item.RechargeCard || 0);
                    map[loc].TwinToys += (item.TwinToys || 0);
                    map[loc].PackAnnif += (item.PackAnnif || 0);
                    map[loc].DailyExpenses += (item.DailyExpenses || 0);
                    map[loc].TotalAmount += (item.TotalAmount || 0);
                  });
                  return Object.values(map).map((item, index) => (
                    <tr key={index}>
                      <td style={{fontWeight: 'bold'}}>Période globale</td>
                      <td>{item.localisation}</td>
                      <td>{formatCurrency(item.PlayZoneStandard)}</td>
                      <td>{formatCurrency(item.PlayZoneSurveillé)}</td>
                      <td>{formatCurrency(item.AccesParent)}</td>
                      <td>{formatCurrency(item.PackQR)}</td>
                      <td>{formatCurrency(item.RechargeQR)}</td>
                      <td>{formatCurrency(item.PackCard)}</td>
                      <td>{formatCurrency(item.RechargeCard)}</td>
                      <td style={{color: '#e91e63', fontWeight: 'bold'}}>{formatCurrency(item.TwinToys)}</td>
                      <td>{formatCurrency(item.PackAnnif)}</td>
                      <td style={{color: '#F44336'}}>{formatCurrency(item.DailyExpenses)}</td>
                      <td style={{color: '#4CAF50', fontWeight: 'bold'}}>{formatCurrency(item.TotalAmount)}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{textAlign: 'center', color: '#777', padding: '20px'}}>Aucun rapport trouvé pour ces dates.</p>
        )}
      </div>

    </div>
  );
};

export default TwinparkStats;
